import { auditLogger, containsMaliciousPatterns, isValidUUID, sanitizeObject } from 'src/utils/security';
import { getCurrentUser, getSupabase } from 'src/utils/supabase';

import { requireAuth, withSecurity } from './secureServiceWrapper';

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  firm_id: string | null;
  firm_name?: string;
  jurisdiction: string;
  title?: string;
  custom_fields?: Record<string, unknown>;
  newsletter_opt_in?: boolean;
  email_notifications_enabled?: boolean;
  weekly_usage_emails_enabled?: boolean;
  updated_at: string;
}

export const profileService = {
  getProfile: async (userId: string): Promise<Profile | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(userId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_user_id', service: 'profile' });
          return null;
        }

        const supabase = await getSupabase();

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select(
            `
          *,
          law_firms:law_firms (
            lawfirmname
          )
        `
          )
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          throw profileError;
        }

        if (!profileData) {
          return null;
        }

        const linkedFirmName = profileData.law_firms?.lawfirmname || null;

        return {
          ...profileData,
          firm_name: linkedFirmName || profileData.firm_name || null,
        };
      },
      { requireAuth: true, rateLimitKey: 'profile_get' }
    );
  },

  getCurrentUserProfile: async (): Promise<Profile | null> => {
    return withSecurity(
      async () => {
        const user = await getCurrentUser();
        if (!user) {
          return null;
        }

        const supabase = await getSupabase();

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select(
            `
          *,
          law_firms:law_firms (
            lawfirmname
          )
        `
          )
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          throw profileError;
        }

        if (!profileData) {
          return null;
        }

        const linkedFirmName = profileData.law_firms?.lawfirmname || null;

        return {
          ...profileData,
          firm_name: linkedFirmName || profileData.firm_name || null,
        };
      },
      { requireAuth: true, rateLimitKey: 'profile_get_current' }
    );
  },

  updateProfile: async (userId: string, updates: Partial<Profile>): Promise<boolean> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(userId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_user_id', service: 'profile' });
          throw new Error('Invalid user ID');
        }

        const currentUserId = await requireAuth();
        if (currentUserId !== userId) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'profile', action: 'update' });
          throw new Error('You can only update your own profile');
        }

        const { custom_fields: customFieldsUpdate, ...restUpdates } = updates;
        const sanitizedUpdates = sanitizeObject(restUpdates as unknown as Record<string, unknown>, {
          placeNameKeys: ['jurisdiction'],
        }) as Partial<Profile>;

        const updateKeys = Object.keys(sanitizedUpdates);
        for (const key of updateKeys) {
          const value = sanitizedUpdates[key as keyof typeof sanitizedUpdates];
          if (typeof value === 'string' && containsMaliciousPatterns(value)) {
            auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'profile', field: key });
            throw new Error('Invalid input detected');
          }
        }

        const supabase = await getSupabase();
        const payload: Record<string, unknown> = {
          ...sanitizedUpdates,
          updated_at: new Date().toISOString(),
        };

        if (customFieldsUpdate !== undefined) {
          const { data: current } = await supabase
            .from('profiles')
            .select('custom_fields')
            .eq('id', userId)
            .maybeSingle();
          const existing = (current?.custom_fields as Record<string, unknown>) || {};
          const merged = { ...existing, ...customFieldsUpdate };
          payload.custom_fields = merged;
        }

        const { error } = await supabase.from('profiles').update(payload).eq('id', userId);

        if (error) {
          throw new Error('Failed to update profile');
        }
        return true;
      },
      { requireAuth: true, rateLimitKey: 'profile_update', rateLimitMax: 30 }
    );
  },

  upsertProfile: async (profileData: Omit<Profile, 'updated_at' | 'firm_name'>): Promise<boolean> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(profileData.id)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_profile_id', service: 'profile' });
          throw new Error('Invalid profile ID');
        }

        const currentUserId = await requireAuth();
        if (currentUserId !== profileData.id) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'profile', action: 'upsert' });
          throw new Error('You can only update your own profile');
        }

        const sanitizedData = sanitizeObject(profileData as unknown as Record<string, unknown>, {
          placeNameKeys: ['jurisdiction'],
        }) as Omit<Profile, 'updated_at' | 'firm_name'>;

        const dataKeys = Object.keys(sanitizedData);
        for (const key of dataKeys) {
          const value = sanitizedData[key as keyof typeof sanitizedData];
          if (typeof value === 'string' && containsMaliciousPatterns(value)) {
            auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'profile', field: key });
            throw new Error('Invalid input detected');
          }
        }

        const supabase = await getSupabase();
        const { error } = await supabase.from('profiles').upsert(
          [
            {
              ...sanitizedData,
              updated_at: new Date().toISOString(),
            },
          ],
          {
            onConflict: 'id',
          }
        );

        if (error) throw error;
        return true;
      },
      { requireAuth: true, rateLimitKey: 'profile_upsert', rateLimitMax: 20 }
    );
  },

  getUserJurisdiction: async (userId: string): Promise<string | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(userId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_user_id', service: 'profile' });
          return null;
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase.from('profiles').select('jurisdiction').eq('id', userId).maybeSingle();

        if (error) throw error;
        return data?.jurisdiction || null;
      },
      { requireAuth: true, rateLimitKey: 'profile_get_jurisdiction' }
    );
  },
};
